import { useState } from "react";
import Reveal from "@/components/Reveal";

const SUBMIT_URL = "https://readdy.ai/api/form/daeote1tto0ceqqnh1sg";

type Status = "idle" | "loading" | "success" | "error";

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // honeypot
    const hp = String(fd.get("website_alt") || "").trim();
    if (hp) {
      setStatus("success");
      setMessage("订阅成功，欢迎加入吃瓜大军，明日瓜报准时送到～");
      form.reset();
      setEmail("");
      return;
    }

    setStatus("loading");
    setMessage("");
    try {
      const payload = new URLSearchParams();
      const entry = fd.get("email");
      if (entry) payload.set("email", String(entry));

      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = {};
      }
      const meta = (parsed.meta ?? {}) as Record<string, unknown>;
      const serverMsg =
        String(meta.message ?? "") || String(meta.detail ?? "") || String(parsed.message ?? "");
      const okCode = parsed.code === "OK";

      if (res.ok && okCode && !/spam/i.test(text)) {
        setStatus("success");
        setMessage("订阅成功，欢迎加入吃瓜大军，明日瓜报准时送到～");
        form.reset();
        setEmail("");
      } else {
        setStatus("error");
        setMessage(serverMsg || "订阅失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络似乎开小差了，请稍后重试。");
    }
  }

  return (
    <section id="subscribe" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        <Reveal>
          <div className="relative overflow-hidden rounded-[28px] bg-accent-500 px-6 py-10 md:px-12 md:py-14">
            <div className="pointer-events-none absolute -right-10 -top-16 h-56 w-56 rounded-full bg-background-50/20 blur-2xl"></div>
            <div className="pointer-events-none absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-primary-600/20 blur-2xl"></div>
            <i className="pointer-events-none absolute right-8 top-8 hidden rotate-12 text-[90px] text-foreground-950/10 md:block ri-megaphone-fill"></i>

            <div className="relative grid items-center gap-8 md:grid-cols-2">
              <div>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-foreground-950 px-3 py-1 text-xs font-bold text-accent-200">
                  <i className="ri-mail-volume-line" aria-hidden="true"></i>
                  每日瓜报 · 免费订阅
                </span>
                <h2 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                  不想错过任何一瓜？
                  <span className="mt-1 block">订阅我们的每日瓜报</span>
                </h2>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-900/80">
                  每天早上一封，汇总前一日最重要的未官宣行程、资源瓜、新戏饼与商务代言动向，一句话带你看懂。
                </p>
              </div>

              <div>
                <form onSubmit={handleSubmit} className="w-full" data-readdy-form="瓜田雷达每日瓜报">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <label htmlFor="nl-email" className="flex-1">
                      <span className="sr-only">邮箱地址</span>
                      <input
                        id="nl-email"
                        name="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="输入你的邮箱地址"
                        className="w-full rounded-full border-2 border-foreground-950/15 bg-background-50 px-5 py-3.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-foreground-950"
                      />
                    </label>
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-600 px-6 py-3.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-700 disabled:opacity-70"
                    >
                      {status === "loading" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true"></i>
                          订阅中
                        </>
                      ) : (
                        <>
                          立即订阅
                          <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
                        </>
                      )}
                    </button>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    autoComplete="off"
                    tabIndex={-1}
                    aria-hidden="true"
                    readOnly
                    className="hp-catch"
                  />

                  {status === "success" && (
                    <p className="animate-fader mt-3 flex items-center gap-1.5 text-sm font-semibold text-foreground-950">
                      <i className="ri-checkbox-circle-fill text-lg" aria-hidden="true"></i>
                      {message}
                    </p>
                  )}
                  {status === "error" && (
                    <p className="animate-fader mt-3 flex items-center gap-1.5 text-sm font-semibold text-foreground-950">
                      <i className="ri-close-circle-fill text-lg" aria-hidden="true"></i>
                      {message}
                    </p>
                  )}
                  {status === "idle" && (
                    <p className="mt-3 flex items-center gap-1.5 text-xs text-foreground-950/70">
                      <i className="ri-lock-2-line" aria-hidden="true"></i>
                      可随时退订，你的邮箱仅用于接收瓜报
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}